import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Mic, Upload, Loader2, Trash2, Sparkles, Lock, AlertCircle,
  Volume2, Trophy, Play,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import {
  getVoiceConfig, listVoiceProfiles, createVoiceProfile,
  deleteVoiceProfile, generateVoice,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const LANG_LABELS = { EN: "English", ES: "Spanish", FR: "French", ZH: "Chinese", JA: "Japanese", KO: "Korean" };

export default function Voice() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [text, setText] = useState("");
  const [language, setLanguage] = useState("EN");
  const [speed, setSpeed] = useState("1.0");
  const [generating, setGenerating] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState(null);

  // Upload form
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([getVoiceConfig(), listVoiceProfiles()]);
      setConfig(c.data);
      setProfiles(p.data?.profiles || []);
      setSelectedId((cur) => cur || p.data?.profiles?.[0]?.id || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load voice config");
    }
  }, []);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      navigate("/auth");
      return;
    }
    refresh();
  }, [userId, navigate, refresh]);

  const handleUpload = async (e) => {
    e?.preventDefault();
    if (!uploadName.trim()) return toast.error("Give your voice a name");
    if (!uploadFile) return toast.error("Pick a voice sample (10–30 s)");
    const fd = new FormData();
    fd.append("name", uploadName.trim());
    fd.append("file", uploadFile);
    setUploading(true);
    try {
      await createVoiceProfile(fd);
      toast.success("Voice profile saved");
      setUploadOpen(false);
      setUploadName("");
      setUploadFile(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this voice profile?")) return;
    try {
      await deleteVoiceProfile(id);
      toast.success("Deleted");
      if (selectedId === id) setSelectedId("");
      await refresh();
    } catch {
      toast.error("Could not delete");
    }
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!selectedId) return toast.error("Pick a voice profile");
    if (!text.trim()) return toast.error("Enter some text");
    setGenerating(true);
    setGeneratedAudio(null);
    try {
      const res = await generateVoice({
        profile_id: selectedId,
        text: text.trim(),
        language,
        speed: parseFloat(speed),
      });
      setGeneratedAudio(res.data);
      toast.success("Audio generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const planGate = config && config.max_profiles === 0;
  const atLimit = config && config.current_profiles >= config.max_profiles;
  const fmtBytes = (n) => `${(n / 1024).toFixed(0)} KB`;

  return (
    <div className="min-h-screen bg-background grain">
      <Seo title="Voice cloning — Sabzy AI" description="Clone your voice from a 10–30s sample and synthesize text in your own voice using OpenVoice V2." path="/voice" noindex />

      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/chat")} data-testid="voice-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Logo />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 lg:px-8 lg:py-16">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
            <Mic className="h-3 w-3" /> Voice cloning · powered by OpenVoice V2
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Speak in your own voice.</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Upload a 10–30 second sample of any voice, then type text to have it spoken back — in that exact voice.
            Saved voice profiles never leave your account.
          </p>
        </div>

        {planGate && (
          <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="voice-plan-gate">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-amber-500/15 text-amber-600">
                <Lock className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">Voice cloning is a paid feature</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Upgrade to unlock — Standard 1 profile · Pro 3 profiles · Pro Max 10 profiles.
                </div>
                <Button onClick={() => navigate("/upgrade")} className="mt-3 rounded-full" size="sm" data-testid="voice-upgrade-cta">
                  See plans
                </Button>
              </div>
            </div>
          </div>
        )}

        {!planGate && config && (
          <div className="grid gap-6 lg:grid-cols-5">
            {/* Profiles column */}
            <section className="space-y-4 lg:col-span-2" data-testid="voice-profiles-section">
              <div className="flex items-end justify-between">
                <div>
                  <h2 className="text-base font-semibold">Your voices</h2>
                  <div className="text-[11px] text-muted-foreground">
                    {config.current_profiles} of {config.max_profiles} used · plan: {config.plan}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setUploadOpen((v) => !v)}
                  disabled={atLimit}
                  className="rounded-full"
                  data-testid="voice-add-btn"
                >
                  {atLimit ? "Limit reached" : <><Upload className="mr-1.5 h-3.5 w-3.5" /> Add</>}
                </Button>
              </div>

              {uploadOpen && !atLimit && (
                <form onSubmit={handleUpload} className="space-y-3 rounded-2xl border border-primary/30 bg-card p-4" data-testid="voice-upload-form">
                  <Input
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder='e.g. "My voice", "Narrator", "Mom"'
                    maxLength={64}
                    className="h-10 rounded-lg"
                    data-testid="voice-name-input"
                  />
                  <Input
                    ref={fileRef}
                    type="file"
                    accept="audio/wav,audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/ogg,audio/webm"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="h-10 rounded-lg cursor-pointer"
                    data-testid="voice-file-input"
                  />
                  <div className="rounded-lg bg-muted/50 px-3 py-2 text-[11px] text-muted-foreground">
                    📎 Best results: clean recording, single speaker, 10–30 seconds, no background music. Max 10 MB.
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={uploading} className="flex-1 rounded-lg" data-testid="voice-upload-submit">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setUploadOpen(false)} className="rounded-lg">
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              {profiles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="voice-empty-state">
                  <Sparkles className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
                  <div className="text-sm font-medium">No voices yet</div>
                  <div className="mt-1 text-xs text-muted-foreground">Upload a sample to get started.</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {profiles.map((p) => {
                    const isSelected = selectedId === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => setSelectedId(p.id)}
                        className={`group flex w-full items-center justify-between rounded-xl border p-3 text-left transition ${
                          isSelected
                            ? "border-primary/60 bg-primary/5 ring-1 ring-primary/30"
                            : "border-border bg-card/40 hover:bg-card"
                        }`}
                        data-testid={`voice-profile-${p.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`grid h-9 w-9 place-items-center rounded-lg ${isSelected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                            <Mic className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{p.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {fmtBytes(p.size_bytes)} · {p.generation_count || 0} clones
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                          className="opacity-0 group-hover:opacity-100"
                          data-testid={`voice-delete-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Generate column */}
            <section className="space-y-4 lg:col-span-3" data-testid="voice-generate-section">
              <div className="rounded-2xl border border-border bg-card/50 p-5">
                <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
                  <Volume2 className="h-4 w-4 text-primary" /> Synthesize speech
                </h2>

                {profiles.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span>Upload a voice profile first.</span>
                  </div>
                ) : (
                  <form onSubmit={handleGenerate} className="space-y-3" data-testid="voice-generate-form">
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="voice-lang-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(config.languages || []).map((l) => (
                            <SelectItem key={l} value={l}>{LANG_LABELS[l] || l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={speed} onValueChange={setSpeed}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="voice-speed-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.75">0.75× slower</SelectItem>
                          <SelectItem value="1.0">1.0× normal</SelectItem>
                          <SelectItem value="1.25">1.25× faster</SelectItem>
                          <SelectItem value="1.5">1.5× fast</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type what you want this voice to say… (≤ 1000 characters)"
                      maxLength={1000}
                      rows={5}
                      className="rounded-lg"
                      data-testid="voice-text-input"
                    />
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] text-muted-foreground">{text.length} / 1000 · Replicate generates in ~30–90 s</div>
                      <Button type="submit" disabled={generating || !selectedId} className="rounded-lg" data-testid="voice-generate-btn">
                        {generating ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cloning…</>
                        ) : (
                          <><Play className="mr-1.5 h-3.5 w-3.5" /> Generate</>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {generatedAudio && (
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 animate-fade-up" data-testid="voice-generated-result">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/15 text-emerald-600">
                      <Trophy className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Audio ready</div>
                      <div className="text-[11px] text-muted-foreground">{fmtBytes(generatedAudio.size_bytes)} · OpenVoice V2</div>
                    </div>
                  </div>
                  <audio
                    controls
                    src={generatedAudio.audio_url}
                    className="w-full"
                    data-testid="voice-audio-player"
                  />
                  <div className="mt-2 flex justify-end">
                    <a
                      href={generatedAudio.audio_url}
                      download={`sabzy-voice-${generatedAudio.id.slice(0, 8)}.wav`}
                      className="text-[11px] text-muted-foreground transition hover:text-primary"
                      data-testid="voice-download-link"
                    >
                      ↓ Download WAV
                    </a>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-border/60 bg-card/30 p-4 text-[11px] leading-relaxed text-muted-foreground">
                💡 <span className="text-foreground">Tip:</span> short, clean voice samples work best.
                Avoid background music or multiple speakers. Each generation typically takes 30–90 seconds and consumes ~$0.09 of your Replicate balance.
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
